import { log } from 'log';
import { pick, prop } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import React, { memo, useCallback } from 'react';
import { useUserSelector } from 'js/store/hooks';
import { useToggleFavoriteGame } from 'js/hooks/games';
import { useShowNotification } from 'js/hooks/useShowNotification';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import * as Types from './types';
import { useDynamicReducerSelector } from '../../../../store';

import style from './GameFavoriteIcon.scss';

export const GameFavoriteIconBare: React.FC<Types.IGameFavoriteIconProps> = ({ game, className = '' }) => {
    const translate = useTranslations();
    const showNotification = useShowNotification();
    const toggleFavoriteGame = useToggleFavoriteGame();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const { favoriteGames, feedName } = useDynamicReducerSelector(pick(['favoriteGames', 'feedName']));
    const { gameCode } = game;
    const isGameFavoriteActive = favoriteGames?.includes(gameCode);

    const onGameFavoriteClick = useCallback(async () => {
        const isFavorite = !isGameFavoriteActive;
        const messageTKey = isFavorite
            ? 'playtech.mobile.system.toast.favoritesAdd'
            : 'playtech.mobile.system.toast.favoritesRemove';

        try {
            await toggleFavoriteGame({ feedName, gameCode, isFavorite, container: 'games-grid' });

            showNotification(messageTKey, { variant: 'info' });
        } catch {
            log('error', '[Games Grid] Failed to toggle favorite game');
        }
    }, [
        gameCode,
        feedName,
        isGameFavoriteActive,
        showNotification,
        toggleFavoriteGame
    ]);

    if (!isLoggedIn) {
        return null;
    }

    return (
        <AccessibilityElement
            onClick={onGameFavoriteClick}
            accessibilityButtons={['Enter', ' ', 'Spacebar']}
            aria-label={isGameFavoriteActive
                ? translate('playtech.mobile.system.games-grid.game.favorite.icon.selected.aria-label')
                : translate('playtech.mobile.system.games-grid.game.favorite.icon.unselected.aria-label')
            }
            className={`${style.gameFavoriteIcon} ${className} ${isGameFavoriteActive ? style.gameFavoriteIconActive : ''} cms-games-grid-game-favorite-icon`}
        />
    );
};

export const GameFavoriteIcon = memo(GameFavoriteIconBare);
